import type {
  AuthProvider,
  PendingSmsCode,
  SecondFactorChallenge,
  SecondFactorKind,
  SecondFactorSummary,
  SignInResult,
  SignOutOptions,
  TotpEnrollment,
} from './authProvider';
import { InvalidPhoneNumberError, InvalidSecondFactorCodeError } from './authErrors';
import { SIGNED_OUT_PATH } from './routes';
import { env } from '@/lib/env';

/**
 * A stub that satisfies the auth boundary without an identity provider.
 *
 * Lets everything except real sign-in — routing, the session model, guards,
 * every error state, the API client — be built and demoed without a Firebase
 * project. Enabled with VITE_AUTH_MODE=fake, which src/lib/env.ts refuses to
 * accept in a production build.
 *
 * Second factors: VITE_FAKE_AUTH_MFA picks what the fake account starts with
 * enrolled — `none` (the default: no challenge at all), `totp` (an
 * authenticator plus a recovery phone) or `sms` (the phone only). Any 6-digit
 * code is accepted except 000000, which fails, so the error states can be
 * demoed too. The token names the factor used at sign-in, and the mock API
 * (TOLLIT_DEV_MFA=enforced, vite/mockApiPlugin.ts) enforces it the way the
 * real backend will.
 *
 * Swapping this for firebaseAuthProvider is a one-line change in
 * authContext.tsx.
 */
const STORAGE_KEY = 'tollit.fakeAuth.signedIn';
const FACTORS_KEY = 'tollit.fakeAuth.secondFactors';
const SIGNED_IN_WITH_KEY = 'tollit.fakeAuth.signedInWith';

/** The one code the fake rejects, so the "wrong code" state can be demoed. */
export const FAKE_REJECTED_CODE = '000000';

function read(key: string): string | null {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string | null): void {
  try {
    if (value === null) window.sessionStorage.removeItem(key);
    else window.sessionStorage.setItem(key, value);
  } catch {
    // Storage blocked: the fake degrades to "signed out", which is safe.
  }
}

function signedIn(): boolean {
  return read(STORAGE_KEY) === 'true';
}

const FAKE_ACCOUNT = {
  username: 'dev.user@tollit.example',
  localAccountId: 'fake-local-account-id',
};

const SEEDED_FACTORS: Record<typeof env.VITE_FAKE_AUTH_MFA, SecondFactorSummary[]> = {
  none: [],
  totp: [
    { id: 'fake-totp-1', kind: 'totp', label: 'Authenticator app' },
    { id: 'fake-sms-1', kind: 'sms', label: 'Recovery phone', maskedPhone: '+*******4512' },
  ],
  sms: [{ id: 'fake-sms-1', kind: 'sms', label: 'Recovery phone', maskedPhone: '+*******4512' }],
};

// Parsed once per distinct stored value, so listSecondFactors() returns the
// same array between changes — useSyncExternalStore needs that.
let cachedRaw: string | null | undefined;
let cachedFactors: readonly SecondFactorSummary[] = [];

function factors(): readonly SecondFactorSummary[] {
  let raw = read(FACTORS_KEY);
  if (raw === null) {
    raw = JSON.stringify(SEEDED_FACTORS[env.VITE_FAKE_AUTH_MFA]);
    write(FACTORS_KEY, raw);
  }
  if (raw !== cachedRaw) {
    cachedRaw = raw;
    try {
      cachedFactors = JSON.parse(raw) as SecondFactorSummary[];
    } catch {
      cachedFactors = [];
    }
  }
  return cachedFactors;
}

function saveFactors(next: readonly SecondFactorSummary[]): void {
  write(FACTORS_KEY, JSON.stringify(next));
}

const listeners = new Set<() => void>();
function notify(): void {
  listeners.forEach((listener) => listener());
}

const challenges = new WeakSet<SecondFactorChallenge>();
const pendingSmsCodes = new WeakMap<PendingSmsCode, { phoneNumber: string | null }>();

function checkCode(code: string): void {
  if (!/^\d{6}$/.test(code) || code === FAKE_REJECTED_CODE) {
    throw new InvalidSecondFactorCodeError();
  }
}

function checkChallenge(challenge: SecondFactorChallenge): void {
  if (!challenges.has(challenge)) throw new Error('Unknown second-factor challenge.');
}

function passwordStep(): SignInResult {
  const enrolled = factors();
  if (enrolled.length === 0) {
    completeSignIn(null);
    return { status: 'signed-in' };
  }
  const challenge: SecondFactorChallenge = { factors: enrolled };
  challenges.add(challenge);
  return { status: 'second-factor-required', challenge };
}

function completeSignIn(kind: SecondFactorKind | null): void {
  const wasSignedIn = signedIn();
  write(STORAGE_KEY, 'true');
  write(SIGNED_IN_WITH_KEY, kind);
  notify();
  // A re-authentication leaves the user where they are; a first sign-in lands
  // on the root route, as before.
  if (!wasSignedIn) window.location.assign('/');
}

/** Runs `run` as a promise: a throw inside it becomes the rejection. */
function settle<T>(run: () => T): Promise<T> {
  return new Promise((resolve) => resolve(run()));
}

let fakeFactorSeq = 0;
function newFactorId(kind: SecondFactorKind): string {
  fakeFactorSeq += 1;
  return `fake-${kind}-${Date.now()}-${fakeFactorSeq}`;
}

export const fakeAuthProvider: AuthProvider = {
  initialize() {
    return Promise.resolve();
  },

  isAuthenticated: signedIn,

  getAccount() {
    // FAKE_ACCOUNT is a module-level constant, not a fresh literal per call: a
    // subscriber reading this through useSyncExternalStore needs the same
    // reference back whenever nothing changed.
    return signedIn() ? FAKE_ACCOUNT : null;
  },

  signIn() {
    // Credentials are ignored: this is a dev escape hatch, not a form to
    // validate. See src/lib/env.ts's build-time guard.
    return Promise.resolve(passwordStep());
  },

  signOut(options: SignOutOptions = {}) {
    write(STORAGE_KEY, null);
    write(SIGNED_IN_WITH_KEY, null);
    notify();
    window.location.assign(options.redirectTo ?? SIGNED_OUT_PATH);
    return Promise.resolve();
  },

  resetPassword() {
    return Promise.resolve();
  },

  getToken() {
    // Names the factor used at sign-in, standing in for the real token's
    // firebase.sign_in_second_factor claim. Only the mock API reads it.
    const kind = read(SIGNED_IN_WITH_KEY);
    return Promise.resolve(kind ? `fake-access-token.mfa-${kind}` : 'fake-access-token');
  },

  subscribe(listener) {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },

  reauthenticate() {
    return Promise.resolve(passwordStep());
  },

  completeSignInWithTotp(challenge, _factorId, code) {
    return settle(() => {
      checkChallenge(challenge);
      checkCode(code);
      completeSignIn('totp');
    });
  },

  sendSignInSms(challenge) {
    return settle(() => {
      checkChallenge(challenge);
      const pending: PendingSmsCode = { brand: 'PendingSmsCode' };
      pendingSmsCodes.set(pending, { phoneNumber: null });
      return pending;
    });
  },

  completeSignInWithSms(pending, code) {
    return settle(() => {
      if (!pendingSmsCodes.has(pending)) throw new Error('Unknown pending SMS code.');
      checkCode(code);
      completeSignIn('sms');
    });
  },

  startTotpEnrollment() {
    const secretKey = 'JBSWY3DPEHPK3PXP';
    const enrollment: TotpEnrollment = {
      secretKey,
      otpauthUri: `otpauth://totp/Tollit:${encodeURIComponent(FAKE_ACCOUNT.username)}?secret=${secretKey}&issuer=Tollit`,
    };
    return Promise.resolve(enrollment);
  },

  confirmTotpEnrollment(_enrollment, code, label) {
    return settle(() => {
      checkCode(code);
      saveFactors([...factors(), { id: newFactorId('totp'), kind: 'totp', label }]);
      notify();
    });
  },

  sendEnrollmentSms(phoneNumber) {
    return settle(() => {
      if (!/^\+[1-9]\d{6,14}$/.test(phoneNumber)) throw new InvalidPhoneNumberError();
      const pending: PendingSmsCode = { brand: 'PendingSmsCode' };
      pendingSmsCodes.set(pending, { phoneNumber });
      return pending;
    });
  },

  confirmPhoneEnrollment(pending, code, label) {
    return settle(() => {
      const phone = pendingSmsCodes.get(pending)?.phoneNumber;
      if (!phone) throw new Error('Unknown pending SMS code.');
      checkCode(code);
      saveFactors([
        ...factors(),
        {
          id: newFactorId('sms'),
          kind: 'sms',
          label,
          maskedPhone: `+${'*'.repeat(phone.length - 5)}${phone.slice(-4)}`,
        },
      ]);
      notify();
    });
  },

  listSecondFactors: factors,

  removeSecondFactor(factorId) {
    saveFactors(factors().filter((factor) => factor.id !== factorId));
    notify();
    return Promise.resolve();
  },
};
